
import { toString, Union, Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { unit_type, array_type, uint32_type, bool_type, bigint_type, class_type, list_type, tuple_type, string_type, option_type, int32_type, union_type, record_type, float64_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { printf, toText, toFail, interpolate } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { int, map } from "../fable_modules/Thoth.Json.10.5.1/Decode.fs.js";
import { stringHash, comparePrimitives, int32ToString, compare, equals, uncurry2 } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { ParameterDefs, paramDefsOfJson, paramDefsToJson, JSONParams_ParameterDefs_$reflection, ParameterDefs_$reflection, slotsToJson, slotsOfJson, JSONParams_ParamSlot_$reflection, ConstrainedExpr_$reflection, ParamSlot_$reflection, ParamDefinition_$reflection, clog2, ParamExpression_$reflection, ParamName_$reflection } from "./ParameterTypes.js";
import { fromInt32, toInt32 } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { FSharpChoice$2 } from "../fable_modules/fable-library-js.5.13.0/Choice.js";
import { find, exists, reverse, cons, empty as empty_1, choose, fold, max as max_1, ofArray, map as map_1, append, tryFind } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { tryFind as tryFind_1, empty, map as map_2, toList, ofList } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { cToInt, pToInt } from "./Ids.js";
import { Optics_fst_, Compose_Lens, Compose_Lens_op_GreaterMinusGreater_Z15C92E89 } from "./Optics.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { empty as empty_2, add, contains } from "../fable_modules/fable-library-js.5.13.0/Set.js";
import { List_distinct } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";

export const Constants_equalityCheckTolerance = 0.0001;

export const Constants_labelPosTolerance = 1E-05;

export const Constants_maxIssieBusWidth = 16384;

/**
 * Position on SVG canvas
 * Positions can be added, subtracted, scaled using overloaded +,-, *  operators
 * currently these custom operators are not used in Issie - they should be!
 */
export class XYPos extends Record {
    constructor(X, Y) {
        super();
        this.X = X;
        this.Y = Y;
    }
}

export function XYPos_$reflection() {
    return record_type("CommonTypes.XYPos", [], XYPos, () => [["X", float64_type], ["Y", float64_type]]);
}

function testXYPosComparison(a, b) {
    const left = a;
    const right = b;
    if (Math.abs(left.X - right.X) <= 1E-07) {
        return Math.abs(left.Y - right.Y) <= 1E-07;
    }
    else {
        return false;
    }
}

/**
 * display XYPos as string nicely for debugging
 */
export function pXY(_arg) {
    const y = _arg.Y;
    const x = _arg.X;
    if (max(Math.abs(x), Math.abs(y)) > 20) {
        return `(${interpolate("%.0f%P()", [x])},${interpolate("%.0f%P()", [y])})`;
    }
    else {
        return `(${interpolate("%.2f%P()", [x])},${interpolate("%.2f%P()", [y])})`;
    }
}

/**
 * Specify the type of a port in a Component.
 */
export class PortType extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Input", "Output"];
    }
    static Input = new PortType(0, []);
    static Output = new PortType(1, []);
}

export function PortType_$reflection() {
    return union_type("CommonTypes.PortType", [], PortType, () => [[], []]);
}

/**
 * A component I/O.
 * 
 * Id (like any other Id) is a string generated with 32 random hex charactes,
 * so it is (practically) globally unique. These Ids are used
 * to uniquely refer to ports and components. They are generated via uuid().
 * 
 * PortNumber is used to identify which port is which on a component, contiguous from 0
 * separately for inputs and outputs. See comments above type definition for details
 * 
 * HostId is the unique Id of the component where the port is. For example,
 * all three ports on the same And component will have the same HostId.
 */
export function componentIdEncoder(cid) {
    return cid;
}

export const componentIdDecoder = (path) => ((value) => map((Item) => (Item | 0), uncurry2(int), path, value));

export class Port extends Record {
    constructor(Id, PortNumber, PortType, HostId) {
        super();
        this.Id = Id;
        this.PortNumber = PortNumber;
        this.PortType = PortType;
        this.HostId = HostId;
    }
}

export function Port_$reflection() {
    return record_type("CommonTypes.Port", [], Port, () => [["Id", int32_type], ["PortNumber", option_type(int32_type)], ["PortType", PortType_$reflection()], ["HostId", int32_type]]);
}

/**
 * This width is for wire displaying, >8 buswires displayed with 8px thickness. Actual size stored in Port type
 */
export class Width extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight"];
    }
    static One = new Width(0, []);
    static Two = new Width(1, []);
    static Three = new Width(2, []);
    static Four = new Width(3, []);
    static Five = new Width(4, []);
    static Six = new Width(5, []);
    static Seven = new Width(6, []);
    static Eight = new Width(7, []);
}

export function Width_$reflection() {
    return union_type("CommonTypes.Width", [], Width, () => [[], [], [], [], [], [], [], []]);
}

export function Width__Text(this$) {
    switch (this$.tag) {
        case 1:
            return "2px";
        case 2:
            return "3px";
        case 3:
            return "4px";
        case 4:
            return "5px";
        case 5:
            return "6px";
        case 6:
            return "7px";
        case 7:
            return "8px";
        default:
            return "1px";
    }
}

/**
 * Type to specify the origin of a custom component
 */
export class CCForm extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["User", "Library", "ProtectedTopLevel", "ProtectedSubSheet", "Verilog"];
    }
    static User = new CCForm(0, []);
    static ProtectedTopLevel = new CCForm(2, []);
    static ProtectedSubSheet = new CCForm(3, []);
}

export function CCForm_$reflection() {
    return union_type("CommonTypes.CCForm", [], CCForm, () => [[], [["LibName", string_type], ["CompName", string_type]], [], [], [["Item", string_type]]]);
}

/**
 * Name identifies the LoadedComponent used.
 * The labels define legends on symbol designating inputs or outputs: and are the names of the Input or Output components of the CC sheet.
 * Label strings are unique per CustomComponent.
 * Label position in list determines inputPortNumber or outputPortNumber of label.
 * Multiple CustomComponent instances are differentiated by Component data.
 */
export class CustomComponentType extends Record {
    constructor(Name, InputLabels, OutputLabels, Form, ParameterBindings, Description) {
        super();
        this.Name = Name;
        this.InputLabels = InputLabels;
        this.OutputLabels = OutputLabels;
        this.Form = Form;
        this.ParameterBindings = ParameterBindings;
        this.Description = Description;
    }
}

export function CustomComponentType_$reflection() {
    return record_type("CommonTypes.CustomComponentType", [], CustomComponentType, () => [["Name", string_type], ["InputLabels", list_type(tuple_type(string_type, int32_type))], ["OutputLabels", list_type(tuple_type(string_type, int32_type))], ["Form", option_type(CCForm_$reflection())], ["ParameterBindings", option_type(class_type("Microsoft.FSharp.Collections.FSharpMap`2", [ParamName_$reflection(), ParamExpression_$reflection()]))], ["Description", option_type(string_type)]]);
}

/**
 * Note that any memory addresses which have not been explicitly set when printing
 * out memory data.
 */
export class Memory extends Record {
    constructor(AddressWidth, WordWidth, Data) {
        super();
        this.AddressWidth = (AddressWidth | 0);
        this.WordWidth = (WordWidth | 0);
        this.Data = Data;
    }
}

export function Memory_$reflection() {
    return record_type("CommonTypes.Memory", [], Memory, () => [["AddressWidth", int32_type], ["WordWidth", int32_type], ["Data", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [bigint_type, bigint_type])]]);
}

export class InitMemData extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["FromData", "FromFile", "ToFile", "ToFileBadName", "UnsignedMultiplier", "SignedMultiplier"];
    }
    static FromData = new InitMemData(0, []);
    static UnsignedMultiplier = new InitMemData(4, []);
    static SignedMultiplier = new InitMemData(5, []);
}

export function InitMemData_$reflection() {
    return union_type("CommonTypes.InitMemData", [], InitMemData, () => [[], [["Item", string_type]], [["Item", string_type]], [["Item", string_type]], [], []]);
}

export class Memory1 extends Record {
    constructor(Init, AddressWidth, WordWidth, Data, Comments) {
        super();
        this.Init = Init;
        this.AddressWidth = (AddressWidth | 0);
        this.WordWidth = (WordWidth | 0);
        this.Data = Data;
        this.Comments = Comments;
    }
}

export function Memory1_$reflection() {
    return record_type("CommonTypes.Memory1", [], Memory1, () => [["Init", InitMemData_$reflection()], ["AddressWidth", int32_type], ["WordWidth", int32_type], ["Data", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [bigint_type, bigint_type])], ["Comments", option_type(class_type("Microsoft.FSharp.Collections.FSharpMap`2", [bigint_type, string_type]))]]);
}

export class ShiftComponentType extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["LSL", "LSR", "ASR"];
    }
    static LSL = new ShiftComponentType(0, []);
    static LSR = new ShiftComponentType(1, []);
    static ASR = new ShiftComponentType(2, []);
}

export function ShiftComponentType_$reflection() {
    return union_type("CommonTypes.ShiftComponentType", [], ShiftComponentType, () => [[], [], []]);
}

/**
 * Number of bits needed on a shifter's SHIFT input to express every shift
 * amount 0 .. busWidth-1. Must be recomputed whenever the bus width changes.
 * 
 * This is ceil(log2 busWidth), clamped to 1 so that a one-bit bus still has a shift input. It
 * used to count the bits of busWidth-1 itself; ParameterTypes.clog2 is the same count, exported
 * because `clog2` in a parameter expression must mean exactly what the SHIFT input does. The one
 * difference is at busWidth <= 0, where counting the bits of -1 shifted for ever.
 */
export function shifterWidthFor(busWidth) {
    return max(1, ~~toInt32(clog2(fromInt32(busWidth)))) | 0;
}

/**
 * Option of this qualifies NBitsXOr to allow many different components
 * None => Xor
 * TODO to reduce technical debt:
 * Rename NbitsXor as NBitsCustom, put all the Nbits ops into this D.U.
 * Change catalog entries for all NBits ops to use NBitsCustom, alter load to remain compatibility.
 */
export class NBitsArithmetic extends Union {
    constructor() {
        super();
        this.tag = 0;
        this.fields = [];
    }
    cases() {
        return ["Multiply"];
    }
    static Multiply = new NBitsArithmetic();
}

export function NBitsArithmetic_$reflection() {
    return union_type("CommonTypes.NBitsArithmetic", [], NBitsArithmetic, () => [[]]);
}

export class ComponentType extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Input1", "Output", "Viewer", "IOLabel", "NotConnected", "BusCompare1", "BusSelection", "Constant1", "Not", "Decode4", "GateN", "Mux2", "Mux4", "Mux8", "Demux2", "Demux4", "Demux8", "NbitsAdder", "NbitsAdderNoCin", "NbitsAdderNoCout", "NbitsAdderNoCinCout", "NbitsXor", "NbitsAnd", "NbitsNot", "NbitsOr", "NbitSpreader", "Custom", "MergeWires", "SplitWire", "MergeN", "SplitN", "DFF", "DFFE", "Register", "RegisterE", "Counter", "CounterNoLoad", "CounterNoEnable", "CounterNoEnableLoad", "AsyncROM1", "ROM1", "RAM1", "AsyncRAM1", "AsyncROM", "ROM", "RAM", "Shift", "BusCompare", "Input", "Constant", "BusOut", "MuxOut", "JoinOut", "JoinIn", "ArrayMerge", "ArrayMux"];
    }
    static IOLabel = new ComponentType(3, []);
    static NotConnected = new ComponentType(4, []);
    static Not = new ComponentType(8, []);
    static Decode4 = new ComponentType(9, []);
    static Mux2 = new ComponentType(11, []);
    static Mux4 = new ComponentType(12, []);
    static Mux8 = new ComponentType(13, []);
    static Demux2 = new ComponentType(14, []);
    static Demux4 = new ComponentType(15, []);
    static Demux8 = new ComponentType(16, []);
    static MergeWires = new ComponentType(27, []);
    static DFF = new ComponentType(31, []);
    static DFFE = new ComponentType(32, []);
}

export function ComponentType_$reflection() {
    return union_type("CommonTypes.ComponentType", [], ComponentType, () => [[["BusWidth", int32_type], ["DefaultValue", option_type(bigint_type)]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [], [], [["BusWidth", int32_type], ["CompareValue", bigint_type], ["DialogTextValue", string_type]], [["OutputWidth", int32_type], ["OutputLSBit", int32_type]], [["Width", int32_type], ["ConstValue", bigint_type], ["DialogTextValue", string_type]], [], [], [["GateType", string_type], ["NumInputs", int32_type]], [], [], [], [], [], [], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type], ["ArithmeticOp", option_type(NBitsArithmetic_$reflection())]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["Item", CustomComponentType_$reflection()]], [], [["BusWidth", int32_type]], [["NumInputs", int32_type]], [["NumInputs", int32_type], ["OutputWdiths", list_type(int32_type)], ["OutputLSBits", list_type(int32_type)]], [], [], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["Item", Memory1_$reflection()]], [["Item", Memory1_$reflection()]], [["Item", Memory1_$reflection()]], [["Item", Memory1_$reflection()]], [["Item", Memory_$reflection()]], [["Item", Memory_$reflection()]], [["Item", Memory_$reflection()]], [["BusWidth", int32_type], ["ShifterWidth", int32_type], ["ShiftType", ShiftComponentType_$reflection()]], [["BusWidth", int32_type], ["CompareValue", bigint_type]], [["BusWidth", int32_type]], [["Width", int32_type], ["ConstValue", bigint_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type], ["Num", int32_type]], [["BusWidth", int32_type], ["Num", int32_type]], [["NumInputs", int32_type]], [["NumInputs", int32_type]]]);
}

/**
 * Active pattern which matches 2-input gate component types.
 * NB - NOT gates are not included here.
 */
export function $007CIsBinaryGate$007CNotBinaryGate$007C(cType) {
    let matchResult, n_1;
    if (cType.tag === 10) {
        if (cType.fields[1] === 2) {
            matchResult = 0;
            n_1 = cType.fields[1];
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return new FSharpChoice$2(/* Choice1Of2 */ 0, [undefined]);
        default:
            return new FSharpChoice$2(/* Choice2Of2 */ 1, [undefined]);
    }
}

export function $007CIsGate$007CNoGate$007C(cType) {
    if (cType.tag === 10) {
        return new FSharpChoice$2(/* Choice1Of2 */ 0, [undefined]);
    }
    else {
        return new FSharpChoice$2(/* Choice2Of2 */ 1, [undefined]);
    }
}

/**
 * get memory component type constructor
 * NB only works with new-style memory components
 */
export function getMemType(cType) {
    switch (cType.tag) {
        case 41:
            return (Item) => (new ComponentType(/* RAM1 */ 41, [Item]));
        case 42:
            return (Item_1) => (new ComponentType(/* AsyncRAM1 */ 42, [Item_1]));
        case 40:
            return (Item_2) => (new ComponentType(/* ROM1 */ 40, [Item_2]));
        case 39:
            return (Item_3) => (new ComponentType(/* AsyncROM1 */ 39, [Item_3]));
        default: {
            const clo = toFail(`Can't get memory type from ${cType}`);
            return clo;
        }
    }
}

export function $007CMemory$007C_$007C(typ) {
    let matchResult, mem;
    switch (typ.tag) {
        case 41: {
            matchResult = 0;
            mem = typ.fields[0];
            break;
        }
        case 42: {
            matchResult = 0;
            mem = typ.fields[0];
            break;
        }
        case 40: {
            matchResult = 0;
            mem = typ.fields[0];
            break;
        }
        case 39: {
            matchResult = 0;
            mem = typ.fields[0];
            break;
        }
        default:
            matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return mem;
        default:
            return undefined;
    }
}

export function $007CMemoryAndType$007C_$007C(typ) {
    switch (typ.tag) {
        case 41:
            return [(Item) => (new ComponentType(/* RAM1 */ 41, [Item])), typ.fields[0]];
        case 42:
            return [(Item_1) => (new ComponentType(/* AsyncRAM1 */ 42, [Item_1])), typ.fields[0]];
        case 40:
            return [(Item_2) => (new ComponentType(/* ROM1 */ 40, [Item_2])), typ.fields[0]];
        case 39:
            return [(Item_3) => (new ComponentType(/* AsyncROM1 */ 39, [Item_3])), typ.fields[0]];
        default:
            return undefined;
    }
}

/**
 * Represents the rotation of a symbol in degrees, Degree0 is the default symbol rotation.
 * Angle is anticlockwise
 */
export class Rotation extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Degree0", "Degree90", "Degree180", "Degree270"];
    }
    static Degree0 = new Rotation(0, []);
    static Degree90 = new Rotation(1, []);
    static Degree180 = new Rotation(2, []);
    static Degree270 = new Rotation(3, []);
}

export function Rotation_$reflection() {
    return union_type("CommonTypes.Rotation", [], Rotation, () => [[], [], [], []]);
}

/**
 * Stores the rotation and the flip of the symbol, flipped false by default
 */
export class STransform extends Record {
    constructor(Rotation, flipped) {
        super();
        this.Rotation = Rotation;
        this.flipped = flipped;
    }
}

export function STransform_$reflection() {
    return record_type("CommonTypes.STransform", [], STransform, () => [["Rotation", Rotation_$reflection()], ["flipped", bool_type]]);
}

/**
 * Represents the sides of a component
 */
export class Edge extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Top", "Bottom", "Left", "Right"];
    }
    static Top = new Edge(0, []);
    static Bottom = new Edge(1, []);
    static Left = new Edge(2, []);
    static Right = new Edge(3, []);
}

export function Edge_$reflection() {
    return union_type("CommonTypes.Edge", [], Edge, () => [[], [], [], []]);
}

/**
 * HLP23: AUTHOR dgs119
 */
export function Edge__get_Opposite(this$) {
    switch (this$.tag) {
        case 0:
            return Edge.Bottom;
        case 1:
            return Edge.Top;
        case 2:
            return Edge.Right;
        default:
            return Edge.Left;
    }
}

/**
 * Holds possible directions to sort ports.
 * HLP23: AUTHOR dgs119
 */
export class Direction extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Clockwise", "AntiClockwise"];
    }
    static Clockwise = new Direction(0, []);
    static AntiClockwise = new Direction(1, []);
}

export function Direction_$reflection() {
    return union_type("CommonTypes.Direction", [], Direction, () => [[], []]);
}

export function Direction__get_Opposite(this$) {
    if (this$.tag === 0) {
        return Direction.AntiClockwise;
    }
    else {
        return Direction.Clockwise;
    }
}

export class BoundingBox extends Record {
    constructor(TopLeft, W, H) {
        super();
        this.TopLeft = TopLeft;
        this.W = W;
        this.H = H;
    }
}

export function BoundingBox_$reflection() {
    return record_type("CommonTypes.BoundingBox", [], BoundingBox, () => [["TopLeft", XYPos_$reflection()], ["W", float64_type], ["H", float64_type]]);
}

export function BoundingBox__Centre(this$) {
    const left = this$.TopLeft;
    const right = new XYPos(this$.W / 2, this$.H / 2);
    return new XYPos(left.X + right.X, left.Y + right.Y);
}

export const topLeft_ = [(a) => a.TopLeft, (s) => ((a_1) => (new BoundingBox(s, a_1.W, a_1.H)))];

export class SymbolInfo extends Record {
    constructor(LabelBoundingBox, LabelRotation, STransform, ReversedInputPorts, PortOrientation, PortOrder, HScale, VScale) {
        super();
        this.LabelBoundingBox = LabelBoundingBox;
        this.LabelRotation = LabelRotation;
        this.STransform = STransform;
        this.ReversedInputPorts = ReversedInputPorts;
        this.PortOrientation = PortOrientation;
        this.PortOrder = PortOrder;
        this.HScale = HScale;
        this.VScale = VScale;
    }
}

export function SymbolInfo_$reflection() {
    return record_type("CommonTypes.SymbolInfo", [], SymbolInfo, () => [["LabelBoundingBox", option_type(BoundingBox_$reflection())], ["LabelRotation", option_type(Rotation_$reflection())], ["STransform", STransform_$reflection()], ["ReversedInputPorts", option_type(bool_type)], ["PortOrientation", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [int32_type, Edge_$reflection()])], ["PortOrder", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [Edge_$reflection(), list_type(int32_type)])], ["HScale", option_type(float64_type)], ["VScale", option_type(float64_type)]]);
}

export const portOrder_ = [(c) => c.PortOrder, (n) => ((c_1) => (new SymbolInfo(c_1.LabelBoundingBox, c_1.LabelRotation, c_1.STransform, c_1.ReversedInputPorts, c_1.PortOrientation, n, c_1.HScale, c_1.VScale)))];

export const portOrientation_ = [(c) => c.PortOrientation, (n) => ((c_1) => (new SymbolInfo(c_1.LabelBoundingBox, c_1.LabelRotation, c_1.STransform, c_1.ReversedInputPorts, n, c_1.PortOrder, c_1.HScale, c_1.VScale)))];

export function getSTransformWithDefault(infoOpt) {
    if (infoOpt != null) {
        return infoOpt.STransform;
    }
    else {
        return new STransform(Rotation.Degree0, false);
    }
}

/**
 * Reduced version of Component: id, type and label are all a simulation needs. A custom
 * component's port names and instance parameter bindings ride inside TypeS
 * (CustomComponentType.InputLabels/OutputLabels/ParameterBindings), where port number n is by
 * definition index n of the label list - so port names are never duplicated here.
 * The array settings of an ARRAY COMPONENT; absent on an ordinary sheet.
 * 
 * An array component is a sheet whose hardware is `Copies` copies of what is drawn on it, one per
 * value of the loop variable. What joins the copies to each other and to the outside is the array
 * IO components - see ComponentType's BusOut, MuxOut, JoinOut and JoinIn.
 * 
 * Copies is a plain integer and deliberately NOT a parameter expression. It fixes how many copies
 * there are, and so fixes the ports of every instance of this sheet: an array component has ONE
 * signature per set of bindings, exactly as any other sheet does. Making it an expression would
 * make a sheet's port LIST depend on who instantiated it, which nothing else in Issie has to cope
 * with. Widths remain parameterisable as they are everywhere else.
 */
export class ArrayInfo extends Record {
    constructor(LoopParam, Copies) {
        super();
        this.LoopParam = LoopParam;
        this.Copies = (Copies | 0);
    }
}

export function ArrayInfo_$reflection() {
    return record_type("CommonTypes.ArrayInfo", [], ArrayInfo, () => [["LoopParam", ParamName_$reflection()], ["Copies", int32_type]]);
}

export const loopParam_ = [(a) => a.LoopParam, (s) => ((a_1) => (new ArrayInfo(s, a_1.Copies)))];

export const copies_ = [(a) => (a.Copies | 0), (s) => ((a_1) => (new ArrayInfo(a_1.LoopParam, s)))];

/**
 * How many copies an array component has.
 */
export function copiesOfArray(info) {
    return info.Copies | 0;
}

export class SimpleComponent extends Record {
    constructor(CompId, TypeS, Label) {
        super();
        this.CompId = (CompId | 0);
        this.TypeS = TypeS;
        this.Label = Label;
    }
}

export function SimpleComponent_$reflection() {
    return record_type("CommonTypes.SimpleComponent", [], SimpleComponent, () => [["CompId", int32_type], ["TypeS", ComponentType_$reflection()], ["Label", string_type]]);
}

/**
 * Reduced version of Connection: two endpoints, each a component id and a port NUMBER
 * (output-side number at the source, input-side number at the destination).
 */
export class SimpleConnection extends Record {
    constructor(ConnId, SrcComp, SrcPort, DestComp, DestPort) {
        super();
        this.ConnId = (ConnId | 0);
        this.SrcComp = (SrcComp | 0);
        this.SrcPort = (SrcPort | 0);
        this.DestComp = (DestComp | 0);
        this.DestPort = (DestPort | 0);
    }
}

export function SimpleConnection_$reflection() {
    return record_type("CommonTypes.SimpleConnection", [], SimpleConnection, () => [["ConnId", int32_type], ["SrcComp", int32_type], ["SrcPort", int32_type], ["DestComp", int32_type], ["DestPort", int32_type]]);
}

/**
 * One sheet of a design. Sheet IO *order* is not carried (in Issie it derives from component
 * geometry, which is gone): the receiving side recovers ordering from any instance's TypeS
 * labels, and the top sheet needs none.
 */
export class SimpleSheet extends Record {
    constructor(SheetName, Components, Connections, DefaultBindings, ParamSlots) {
        super();
        this.SheetName = SheetName;
        this.Components = Components;
        this.Connections = Connections;
        this.DefaultBindings = DefaultBindings;
        this.ParamSlots = ParamSlots;
    }
}

export function SimpleSheet_$reflection() {
    return record_type("CommonTypes.SimpleSheet", [], SimpleSheet, () => [["SheetName", string_type], ["Components", list_type(SimpleComponent_$reflection())], ["Connections", list_type(SimpleConnection_$reflection())], ["DefaultBindings", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [ParamName_$reflection(), ParamDefinition_$reflection()])], ["ParamSlots", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [ParamSlot_$reflection(), ConstrainedExpr_$reflection()])]]);
}

/**
 * A whole design: the sheets and which one is the top.
 */
export class SimpleDesign extends Record {
    constructor(TopSheet, Sheets) {
        super();
        this.TopSheet = TopSheet;
        this.Sheets = Sheets;
    }
}

export function SimpleDesign_$reflection() {
    return record_type("CommonTypes.SimpleDesign", [], SimpleDesign, () => [["TopSheet", string_type], ["Sheets", list_type(SimpleSheet_$reflection())]]);
}

/**
 * JSComponent mapped to F# record.
 * Id uniquely identifies the component within a sheet.
 * Label is optional descriptor displayed on schematic.
 */
export class Component extends Record {
    constructor(Id, Type, Label, InputPorts, OutputPorts, X, Y, H, W, SymbolInfo, SlotInfo) {
        super();
        this.Id = Id;
        this.Type = Type;
        this.Label = Label;
        this.InputPorts = InputPorts;
        this.OutputPorts = OutputPorts;
        this.X = X;
        this.Y = Y;
        this.H = H;
        this.W = W;
        this.SymbolInfo = SymbolInfo;
        this.SlotInfo = SlotInfo;
    }
}

export function Component_$reflection() {
    return record_type("CommonTypes.Component", [], Component, () => [["Id", int32_type], ["Type", ComponentType_$reflection()], ["Label", string_type], ["InputPorts", list_type(Port_$reflection())], ["OutputPorts", list_type(Port_$reflection())], ["X", float64_type], ["Y", float64_type], ["H", float64_type], ["W", float64_type], ["SymbolInfo", option_type(SymbolInfo_$reflection())], ["SlotInfo", option_type(class_type("Microsoft.FSharp.Collections.FSharpMap`2", [ParamSlot_$reflection(), ConstrainedExpr_$reflection()]))]]);
}

export function Component__getPort_307C1D41(this$, portId) {
    return tryFind((port) => equals(port.Id, portId), append(this$.InputPorts, this$.OutputPorts));
}

/**
 * Equality function for components, includes all geometry except component position
 */
export function Component__isSame_596CF542(c1, c2) {
    let arg0__4, arg0__7;
    if (((((equals(c1.Id, c2.Id) && equals(c1.Type, c2.Type)) && (c1.Label === c2.Label)) && equals(c1.SlotInfo, c2.SlotInfo)) && equals(c1.InputPorts, c2.InputPorts)) && equals(c1.OutputPorts, c2.OutputPorts)) {
        const matchValue = c1.SymbolInfo;
        const matchValue_1 = c2.SymbolInfo;
        let matchResult, s1, s2;
        if (matchValue == null) {
            if (matchValue_1 == null) {
                matchResult = 1;
            }
            else {
                matchResult = 2;
            }
        }
        else if (matchValue_1 != null) {
            matchResult = 0;
            s1 = matchValue;
            s2 = matchValue_1;
        }
        else {
            matchResult = 2;
        }
        switch (matchResult) {
            case 0: {
                const dx = c1.X - c2.X;
                const dy = c1.Y - c2.Y;
                let labelPosEq;
                const matchValue_3 = s1.LabelBoundingBox;
                const matchValue_4 = s2.LabelBoundingBox;
                let matchResult_1, l1, l2;
                if (matchValue_3 == null) {
                    if (matchValue_4 == null) {
                        matchResult_1 = 1;
                    }
                    else {
                        matchResult_1 = 2;
                    }
                }
                else if (matchValue_4 != null) {
                    matchResult_1 = 0;
                    l1 = matchValue_3;
                    l2 = matchValue_4;
                }
                else {
                    matchResult_1 = 2;
                }
                switch (matchResult_1) {
                    case 0: {
                        labelPosEq = ((((arg0__4 = ((l1.TopLeft.X - l2.TopLeft.X) - dx), Math.pow(arg0__4, 2))) + ((arg0__7 = ((l1.TopLeft.Y - l2.TopLeft.Y) - dy), Math.pow(arg0__7, 2)))) < Constants_labelPosTolerance);
                        break;
                    }
                    case 1: {
                        labelPosEq = true;
                        break;
                    }
                    default:
                        labelPosEq = false;
                }
                if (((((equals(s1.HScale, s2.HScale) && equals(s1.VScale, s2.VScale)) && equals(s1.LabelRotation, s2.LabelRotation)) && s1.PortOrder.Equals(s2.PortOrder)) && s1.PortOrientation.Equals(s2.PortOrientation)) && equals(s1.ReversedInputPorts, s2.ReversedInputPorts)) {
                    return labelPosEq;
                }
                else {
                    return false;
                }
            }
            case 1:
                return true;
            default:
                return false;
        }
    }
    else {
        return false;
    }
}

export const type_ = [(c) => c.Type, (n) => ((c_1) => (new Component(c_1.Id, n, c_1.Label, c_1.InputPorts, c_1.OutputPorts, c_1.X, c_1.Y, c_1.H, c_1.W, c_1.SymbolInfo, c_1.SlotInfo)))];

export const inputPorts_ = [(c) => c.InputPorts, (n) => ((c_1) => (new Component(c_1.Id, c_1.Type, c_1.Label, n, c_1.OutputPorts, c_1.X, c_1.Y, c_1.H, c_1.W, c_1.SymbolInfo, c_1.SlotInfo)))];

export const outputPorts_ = [(c) => c.OutputPorts, (n) => ((c_1) => (new Component(c_1.Id, c_1.Type, c_1.Label, c_1.InputPorts, n, c_1.X, c_1.Y, c_1.H, c_1.W, c_1.SymbolInfo, c_1.SlotInfo)))];

export const h_ = [(c) => c.H, (n) => ((c_1) => (new Component(c_1.Id, c_1.Type, c_1.Label, c_1.InputPorts, c_1.OutputPorts, c_1.X, c_1.Y, n, c_1.W, c_1.SymbolInfo, c_1.SlotInfo)))];

export const w_ = [(c) => c.W, (n) => ((c_1) => (new Component(c_1.Id, c_1.Type, c_1.Label, c_1.InputPorts, c_1.OutputPorts, c_1.X, c_1.Y, c_1.H, n, c_1.SymbolInfo, c_1.SlotInfo)))];

export const slotInfo_ = [(c) => c.SlotInfo, (n) => ((c_1) => (new Component(c_1.Id, c_1.Type, c_1.Label, c_1.InputPorts, c_1.OutputPorts, c_1.X, c_1.Y, c_1.H, c_1.W, c_1.SymbolInfo, n)))];

/**
 * JSConnection mapped to F# record.
 * Id uniquely identifies connection globally and is used by library.
 */
export class Connection extends Record {
    constructor(Id, Source, Target, Vertices) {
        super();
        this.Id = Id;
        this.Source = Source;
        this.Target = Target;
        this.Vertices = Vertices;
    }
}

export function Connection_$reflection() {
    return record_type("CommonTypes.Connection", [], Connection, () => [["Id", int32_type], ["Source", Port_$reflection()], ["Target", Port_$reflection()], ["Vertices", list_type(tuple_type(float64_type, float64_type, bool_type))]]);
}

/**
 * reduced version of CanvasState for electrical comparison, all geometry removed, components ordered
 */
export class ReducedCanvasState extends Union {
    constructor(Item) {
        super();
        this.tag = 0;
        this.fields = [Item];
    }
    cases() {
        return ["ReducedCanvasState"];
    }
}

export function ReducedCanvasState_$reflection() {
    return union_type("CommonTypes.ReducedCanvasState", [], ReducedCanvasState, () => [[["Item", tuple_type(list_type(Component_$reflection()), list_type(Connection_$reflection()))]]]);
}

export function unreduced(_arg) {
    return [_arg.fields[0][0], _arg.fields[0][1]];
}

/**
 * Used only to read/write .dgm files, which may contain legacy ComponentType D.U. cases no longer used
 * Any NEW case added to ComponentType must also be added here
 * Cases DELETED from ComponentType should remain here, with a conversion added.
 */
export class JSONComponent_ComponentType extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Input1", "Output", "Viewer", "IOLabel", "NotConnected", "BusCompare1", "BusSelection", "Constant1", "Not", "And", "Or", "Xor", "Nand", "Nor", "Xnor", "Decode4", "GateN", "Mux2", "Mux4", "Mux8", "Demux2", "Demux4", "Demux8", "NbitsAdder", "NbitsAdderNoCin", "NbitsAdderNoCout", "NbitsAdderNoCinCout", "NbitsXor", "NbitsAnd", "NbitsNot", "NbitsOr", "NbitSpreader", "Custom", "MergeWires", "SplitWire", "MergeN", "SplitN", "DFF", "DFFE", "Register", "RegisterE", "Counter", "CounterNoLoad", "CounterNoEnable", "CounterNoEnableLoad", "AsyncROM1", "ROM1", "RAM1", "AsyncRAM1", "AsyncROM", "ROM", "RAM", "Shift", "BusCompare", "Input", "Constant", "BusOut", "MuxOut", "JoinOut", "JoinIn", "ArrayMerge", "ArrayMux"];
    }
    static IOLabel = new JSONComponent_ComponentType(3, []);
    static NotConnected = new JSONComponent_ComponentType(4, []);
    static Not = new JSONComponent_ComponentType(8, []);
    static And = new JSONComponent_ComponentType(9, []);
    static Or = new JSONComponent_ComponentType(10, []);
    static Xor = new JSONComponent_ComponentType(11, []);
    static Nand = new JSONComponent_ComponentType(12, []);
    static Nor = new JSONComponent_ComponentType(13, []);
    static Xnor = new JSONComponent_ComponentType(14, []);
    static Decode4 = new JSONComponent_ComponentType(15, []);
    static Mux2 = new JSONComponent_ComponentType(17, []);
    static Mux4 = new JSONComponent_ComponentType(18, []);
    static Mux8 = new JSONComponent_ComponentType(19, []);
    static Demux2 = new JSONComponent_ComponentType(20, []);
    static Demux4 = new JSONComponent_ComponentType(21, []);
    static Demux8 = new JSONComponent_ComponentType(22, []);
    static MergeWires = new JSONComponent_ComponentType(33, []);
    static DFF = new JSONComponent_ComponentType(37, []);
    static DFFE = new JSONComponent_ComponentType(38, []);
}

export function JSONComponent_ComponentType_$reflection() {
    return union_type("CommonTypes.JSONComponent.ComponentType", [], JSONComponent_ComponentType, () => [[["BusWidth", int32_type], ["DefaultValue", option_type(bigint_type)]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [], [], [["BusWidth", int32_type], ["CompareValue", bigint_type], ["DialogTextValue", string_type]], [["OutputWidth", int32_type], ["OutputLSBit", int32_type]], [["Width", int32_type], ["ConstValue", bigint_type], ["DialogTextValue", string_type]], [], [], [], [], [], [], [], [], [["GateType", string_type], ["NumInputs", int32_type]], [], [], [], [], [], [], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type], ["ArithmeticOp", option_type(NBitsArithmetic_$reflection())]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["Item", CustomComponentType_$reflection()]], [], [["BusWidth", int32_type]], [["NumInputs", int32_type]], [["NumInputs", int32_type], ["OutputWdiths", list_type(int32_type)], ["OutputLSBits", list_type(int32_type)]], [], [], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["Item", Memory1_$reflection()]], [["Item", Memory1_$reflection()]], [["Item", Memory1_$reflection()]], [["Item", Memory1_$reflection()]], [["Item", Memory_$reflection()]], [["Item", Memory_$reflection()]], [["Item", Memory_$reflection()]], [["BusWidth", int32_type], ["ShifterWidth", int32_type], ["ShiftType", ShiftComponentType_$reflection()]], [["BusWidth", int32_type], ["CompareValue", bigint_type]], [["BusWidth", int32_type]], [["Width", int32_type], ["ConstValue", bigint_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type]], [["BusWidth", int32_type], ["Num", int32_type]], [["BusWidth", int32_type], ["Num", int32_type]], [["NumInputs", int32_type]], [["NumInputs", int32_type]]]);
}

/**
 * The FILE form of a Port. Saved .dgm files hold ids as strings - uuids in old files,
 * integers written as strings in new ones - while the in-memory types hold integers; the
 * converters below are where one becomes the other. Field names match the in-memory type
 * exactly, so the on-disk JSON is unchanged by the int move.
 */
export class JSONComponent_Port extends Record {
    constructor(Id, PortNumber, PortType, HostId) {
        super();
        this.Id = Id;
        this.PortNumber = PortNumber;
        this.PortType = PortType;
        this.HostId = HostId;
    }
}

export function JSONComponent_Port_$reflection() {
    return record_type("CommonTypes.JSONComponent.Port", [], JSONComponent_Port, () => [["Id", string_type], ["PortNumber", option_type(int32_type)], ["PortType", PortType_$reflection()], ["HostId", string_type]]);
}

/**
 * The FILE form of SymbolInfo: port ids (map keys and PortOrder values) as strings.
 */
export class JSONComponent_SymbolInfo extends Record {
    constructor(LabelBoundingBox, LabelRotation, STransform, ReversedInputPorts, PortOrientation, PortOrder, HScale, VScale) {
        super();
        this.LabelBoundingBox = LabelBoundingBox;
        this.LabelRotation = LabelRotation;
        this.STransform = STransform;
        this.ReversedInputPorts = ReversedInputPorts;
        this.PortOrientation = PortOrientation;
        this.PortOrder = PortOrder;
        this.HScale = HScale;
        this.VScale = VScale;
    }
}

export function JSONComponent_SymbolInfo_$reflection() {
    return record_type("CommonTypes.JSONComponent.SymbolInfo", [], JSONComponent_SymbolInfo, () => [["LabelBoundingBox", option_type(BoundingBox_$reflection())], ["LabelRotation", option_type(Rotation_$reflection())], ["STransform", STransform_$reflection()], ["ReversedInputPorts", option_type(bool_type)], ["PortOrientation", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [string_type, Edge_$reflection()])], ["PortOrder", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [Edge_$reflection(), list_type(string_type)])], ["HScale", option_type(float64_type)], ["VScale", option_type(float64_type)]]);
}

/**
 * Like Component, but with legacy cases added to ComponentType
 * Used only to read/write JSON versions of circuits
 */
export class JSONComponent_Component extends Record {
    constructor(Id, Type, Label, InputPorts, OutputPorts, SlotInfo, X, Y, H, W, SymbolInfo) {
        super();
        this.Id = Id;
        this.Type = Type;
        this.Label = Label;
        this.InputPorts = InputPorts;
        this.OutputPorts = OutputPorts;
        this.SlotInfo = SlotInfo;
        this.X = X;
        this.Y = Y;
        this.H = H;
        this.W = W;
        this.SymbolInfo = SymbolInfo;
    }
}

export function JSONComponent_Component_$reflection() {
    return record_type("CommonTypes.JSONComponent.Component", [], JSONComponent_Component, () => [["Id", string_type], ["Type", JSONComponent_ComponentType_$reflection()], ["Label", string_type], ["InputPorts", list_type(JSONComponent_Port_$reflection())], ["OutputPorts", list_type(JSONComponent_Port_$reflection())], ["SlotInfo", option_type(class_type("Microsoft.FSharp.Collections.FSharpMap`2", [JSONParams_ParamSlot_$reflection(), ConstrainedExpr_$reflection()]))], ["X", float64_type], ["Y", float64_type], ["H", float64_type], ["W", float64_type], ["SymbolInfo", option_type(JSONComponent_SymbolInfo_$reflection())]]);
}

/**
 * The FILE form of a Connection: ids as strings.
 */
export class JSONComponent_Connection extends Record {
    constructor(Id, Source, Target, Vertices) {
        super();
        this.Id = Id;
        this.Source = Source;
        this.Target = Target;
        this.Vertices = Vertices;
    }
}

export function JSONComponent_Connection_$reflection() {
    return record_type("CommonTypes.JSONComponent.Connection", [], JSONComponent_Connection, () => [["Id", string_type], ["Source", JSONComponent_Port_$reflection()], ["Target", JSONComponent_Port_$reflection()], ["Vertices", list_type(tuple_type(float64_type, float64_type, bool_type))]]);
}

/**
 * Transforms JSON components (parsed from JSON) to current components: legacy ComponentType
 * cases are upgraded, and the file's string ids become integers through the mapping functions
 * the loader supplies - which is where a uuid in an old file gets its integer allocated.
 */
export function convertFromJSONComponent(mapCompId, mapPortId, comp) {
    let option_2, info;
    const newPort = (port) => (new Port(mapPortId(port.Id), port.PortNumber, port.PortType, mapCompId(port.HostId)));
    const Id = mapCompId(comp.Id);
    let Type;
    const ct = comp.Type;
    switch (ct.tag) {
        case 1: {
            Type = (new ComponentType(/* Output */ 1, [ct.fields[0]]));
            break;
        }
        case 2: {
            Type = (new ComponentType(/* Viewer */ 2, [ct.fields[0]]));
            break;
        }
        case 3: {
            Type = ComponentType.IOLabel;
            break;
        }
        case 4: {
            Type = ComponentType.NotConnected;
            break;
        }
        case 5: {
            Type = (new ComponentType(/* BusCompare1 */ 5, [ct.fields[0], ct.fields[1], ct.fields[2]]));
            break;
        }
        case 6: {
            Type = (new ComponentType(/* BusSelection */ 6, [ct.fields[0], ct.fields[1]]));
            break;
        }
        case 7: {
            Type = (new ComponentType(/* Constant1 */ 7, [ct.fields[0], ct.fields[1], ct.fields[2]]));
            break;
        }
        case 8: {
            Type = ComponentType.Not;
            break;
        }
        case 9: {
            Type = (new ComponentType(/* GateN */ 10, ["and", 2]));
            break;
        }
        case 10: {
            Type = (new ComponentType(/* GateN */ 10, ["or", 2]));
            break;
        }
        case 11: {
            Type = (new ComponentType(/* GateN */ 10, ["xor", 2]));
            break;
        }
        case 12: {
            Type = (new ComponentType(/* GateN */ 10, ["nand", 2]));
            break;
        }
        case 13: {
            Type = (new ComponentType(/* GateN */ 10, ["nor", 2]));
            break;
        }
        case 14: {
            Type = (new ComponentType(/* GateN */ 10, ["xnor", 2]));
            break;
        }
        case 16: {
            Type = (new ComponentType(/* GateN */ 10, [ct.fields[0], ct.fields[1]]));
            break;
        }
        case 15: {
            Type = ComponentType.Decode4;
            break;
        }
        case 17: {
            Type = ComponentType.Mux2;
            break;
        }
        case 18: {
            Type = ComponentType.Mux4;
            break;
        }
        case 19: {
            Type = ComponentType.Mux8;
            break;
        }
        case 20: {
            Type = ComponentType.Demux2;
            break;
        }
        case 21: {
            Type = ComponentType.Demux4;
            break;
        }
        case 22: {
            Type = ComponentType.Demux8;
            break;
        }
        case 23: {
            Type = (new ComponentType(/* NbitsAdder */ 17, [ct.fields[0]]));
            break;
        }
        case 24: {
            Type = (new ComponentType(/* NbitsAdderNoCin */ 18, [ct.fields[0]]));
            break;
        }
        case 25: {
            Type = (new ComponentType(/* NbitsAdderNoCout */ 19, [ct.fields[0]]));
            break;
        }
        case 26: {
            Type = (new ComponentType(/* NbitsAdderNoCinCout */ 20, [ct.fields[0]]));
            break;
        }
        case 27: {
            Type = (new ComponentType(/* NbitsXor */ 21, [ct.fields[0], ct.fields[1]]));
            break;
        }
        case 28: {
            Type = (new ComponentType(/* NbitsAnd */ 22, [ct.fields[0]]));
            break;
        }
        case 29: {
            Type = (new ComponentType(/* NbitsNot */ 23, [ct.fields[0]]));
            break;
        }
        case 30: {
            Type = (new ComponentType(/* NbitsOr */ 24, [ct.fields[0]]));
            break;
        }
        case 31: {
            Type = (new ComponentType(/* NbitSpreader */ 25, [ct.fields[0]]));
            break;
        }
        case 32: {
            Type = (new ComponentType(/* Custom */ 26, [ct.fields[0]]));
            break;
        }
        case 33: {
            Type = ComponentType.MergeWires;
            break;
        }
        case 35: {
            Type = (new ComponentType(/* MergeN */ 29, [ct.fields[0]]));
            break;
        }
        case 34: {
            Type = (new ComponentType(/* SplitWire */ 28, [ct.fields[0]]));
            break;
        }
        case 36: {
            Type = (new ComponentType(/* SplitN */ 30, [ct.fields[0], ct.fields[1], ct.fields[2]]));
            break;
        }
        case 37: {
            Type = ComponentType.DFF;
            break;
        }
        case 38: {
            Type = ComponentType.DFFE;
            break;
        }
        case 39: {
            Type = (new ComponentType(/* Register */ 33, [ct.fields[0]]));
            break;
        }
        case 40: {
            Type = (new ComponentType(/* RegisterE */ 34, [ct.fields[0]]));
            break;
        }
        case 41: {
            Type = (new ComponentType(/* Counter */ 35, [ct.fields[0]]));
            break;
        }
        case 42: {
            Type = (new ComponentType(/* CounterNoLoad */ 36, [ct.fields[0]]));
            break;
        }
        case 43: {
            Type = (new ComponentType(/* CounterNoEnable */ 37, [ct.fields[0]]));
            break;
        }
        case 44: {
            Type = (new ComponentType(/* CounterNoEnableLoad */ 38, [ct.fields[0]]));
            break;
        }
        case 45: {
            Type = (new ComponentType(/* AsyncROM1 */ 39, [ct.fields[0]]));
            break;
        }
        case 46: {
            Type = (new ComponentType(/* ROM1 */ 40, [ct.fields[0]]));
            break;
        }
        case 47: {
            Type = (new ComponentType(/* RAM1 */ 41, [ct.fields[0]]));
            break;
        }
        case 48: {
            Type = (new ComponentType(/* AsyncRAM1 */ 42, [ct.fields[0]]));
            break;
        }
        case 49: {
            Type = (new ComponentType(/* AsyncROM */ 43, [ct.fields[0]]));
            break;
        }
        case 50: {
            Type = (new ComponentType(/* ROM */ 44, [ct.fields[0]]));
            break;
        }
        case 51: {
            Type = (new ComponentType(/* RAM */ 45, [ct.fields[0]]));
            break;
        }
        case 52: {
            Type = (new ComponentType(/* Shift */ 46, [ct.fields[0], ct.fields[1], ct.fields[2]]));
            break;
        }
        case 56: {
            Type = (new ComponentType(/* BusOut */ 50, [ct.fields[0]]));
            break;
        }
        case 57: {
            Type = (new ComponentType(/* MuxOut */ 51, [ct.fields[0]]));
            break;
        }
        case 58: {
            Type = (new ComponentType(/* JoinOut */ 52, [ct.fields[0], ct.fields[1]]));
            break;
        }
        case 59: {
            Type = (new ComponentType(/* JoinIn */ 53, [ct.fields[0], ct.fields[1]]));
            break;
        }
        case 60: {
            Type = (new ComponentType(/* ArrayMerge */ 54, [ct.fields[0]]));
            break;
        }
        case 61: {
            Type = (new ComponentType(/* ArrayMux */ 55, [ct.fields[0]]));
            break;
        }
        case 55: {
            const v = ct.fields[1];
            Type = (new ComponentType(/* Constant1 */ 7, [ct.fields[0], v, toText(printf("%A"))(v)]));
            break;
        }
        case 54: {
            Type = (new ComponentType(/* Input1 */ 0, [ct.fields[0], undefined]));
            break;
        }
        case 53: {
            const v_1 = ct.fields[1];
            Type = (new ComponentType(/* BusCompare1 */ 5, [ct.fields[0], v_1, toText(printf("%A"))(v_1)]));
            break;
        }
        default:
            Type = (new ComponentType(/* Input1 */ 0, [ct.fields[0], ct.fields[1]]));
    }
    const InputPorts = map_1(newPort, comp.InputPorts);
    const OutputPorts = map_1(newPort, comp.OutputPorts);
    let SlotInfo;
    const option_1 = comp.SlotInfo;
    SlotInfo = ((option_1 != null) ? slotsOfJson(mapCompId, option_1) : undefined);
    return new Component(Id, Type, comp.Label, InputPorts, OutputPorts, comp.X, comp.Y, comp.H, comp.W, (option_2 = comp.SymbolInfo, (option_2 != null) ? ((info = option_2, new SymbolInfo(info.LabelBoundingBox, info.LabelRotation, info.STransform, info.ReversedInputPorts, ofList(map_1((tupledArg) => [mapPortId(tupledArg[0]), tupledArg[1]], toList(info.PortOrientation)), {
        Compare: (x_26, y) => (compare(x_26, y) | 0),
    }), map_2((_arg, ids) => map_1(mapPortId, ids), info.PortOrder), info.HScale, info.VScale))) : undefined), SlotInfo);
}

/**
 * A file connection to a live one, through the same id mappings.
 */
export function convertFromJSONConnection(mapConnId, mapCompId, mapPortId, conn) {
    const newPort = (port) => (new Port(mapPortId(port.Id), port.PortNumber, port.PortType, mapCompId(port.HostId)));
    return new Connection(mapConnId(conn.Id), newPort(conn.Source), newPort(conn.Target), conn.Vertices);
}

/**
 * Transforms normal Components into JSON Components which can be saved.
 * This is always an identity transformation since the normal ComponentType
 * muts be strict subset of teh JSON ComponentType.
 * unboxing is ok here because we do not use equality in the conversion to JSON.
 */
export function convertToJSONComponent(comp) {
    let option_1, option_2, info;
    let newType;
    const matchValue = comp.Type;
    newType = ((matchValue.tag === 1) ? (new JSONComponent_ComponentType(/* Output */ 1, [matchValue.fields[0]])) : ((matchValue.tag === 2) ? (new JSONComponent_ComponentType(/* Viewer */ 2, [matchValue.fields[0]])) : ((matchValue.tag === 3) ? JSONComponent_ComponentType.IOLabel : ((matchValue.tag === 4) ? JSONComponent_ComponentType.NotConnected : ((matchValue.tag === 5) ? (new JSONComponent_ComponentType(/* BusCompare1 */ 5, [matchValue.fields[0], matchValue.fields[1], matchValue.fields[2]])) : ((matchValue.tag === 6) ? (new JSONComponent_ComponentType(/* BusSelection */ 6, [matchValue.fields[0], matchValue.fields[1]])) : ((matchValue.tag === 7) ? (new JSONComponent_ComponentType(/* Constant1 */ 7, [matchValue.fields[0], matchValue.fields[1], matchValue.fields[2]])) : ((matchValue.tag === 8) ? JSONComponent_ComponentType.Not : ((matchValue.tag === 9) ? JSONComponent_ComponentType.Decode4 : ((matchValue.tag === 10) ? (new JSONComponent_ComponentType(/* GateN */ 16, [matchValue.fields[0], matchValue.fields[1]])) : ((matchValue.tag === 11) ? JSONComponent_ComponentType.Mux2 : ((matchValue.tag === 12) ? JSONComponent_ComponentType.Mux4 : ((matchValue.tag === 13) ? JSONComponent_ComponentType.Mux8 : ((matchValue.tag === 14) ? JSONComponent_ComponentType.Demux2 : ((matchValue.tag === 15) ? JSONComponent_ComponentType.Demux4 : ((matchValue.tag === 16) ? JSONComponent_ComponentType.Demux8 : ((matchValue.tag === 17) ? (new JSONComponent_ComponentType(/* NbitsAdder */ 23, [matchValue.fields[0]])) : ((matchValue.tag === 18) ? (new JSONComponent_ComponentType(/* NbitsAdderNoCin */ 24, [matchValue.fields[0]])) : ((matchValue.tag === 19) ? (new JSONComponent_ComponentType(/* NbitsAdderNoCout */ 25, [matchValue.fields[0]])) : ((matchValue.tag === 20) ? (new JSONComponent_ComponentType(/* NbitsAdderNoCinCout */ 26, [matchValue.fields[0]])) : ((matchValue.tag === 21) ? (new JSONComponent_ComponentType(/* NbitsXor */ 27, [matchValue.fields[0], matchValue.fields[1]])) : ((matchValue.tag === 22) ? (new JSONComponent_ComponentType(/* NbitsAnd */ 28, [matchValue.fields[0]])) : ((matchValue.tag === 23) ? (new JSONComponent_ComponentType(/* NbitsNot */ 29, [matchValue.fields[0]])) : ((matchValue.tag === 24) ? (new JSONComponent_ComponentType(/* NbitsOr */ 30, [matchValue.fields[0]])) : ((matchValue.tag === 25) ? (new JSONComponent_ComponentType(/* NbitSpreader */ 31, [matchValue.fields[0]])) : ((matchValue.tag === 26) ? (new JSONComponent_ComponentType(/* Custom */ 32, [matchValue.fields[0]])) : ((matchValue.tag === 27) ? JSONComponent_ComponentType.MergeWires : ((matchValue.tag === 29) ? (new JSONComponent_ComponentType(/* MergeN */ 35, [matchValue.fields[0]])) : ((matchValue.tag === 28) ? (new JSONComponent_ComponentType(/* SplitWire */ 34, [matchValue.fields[0]])) : ((matchValue.tag === 30) ? (new JSONComponent_ComponentType(/* SplitN */ 36, [matchValue.fields[0], matchValue.fields[1], matchValue.fields[2]])) : ((matchValue.tag === 31) ? JSONComponent_ComponentType.DFF : ((matchValue.tag === 32) ? JSONComponent_ComponentType.DFFE : ((matchValue.tag === 33) ? (new JSONComponent_ComponentType(/* Register */ 39, [matchValue.fields[0]])) : ((matchValue.tag === 34) ? (new JSONComponent_ComponentType(/* RegisterE */ 40, [matchValue.fields[0]])) : ((matchValue.tag === 35) ? (new JSONComponent_ComponentType(/* Counter */ 41, [matchValue.fields[0]])) : ((matchValue.tag === 36) ? (new JSONComponent_ComponentType(/* CounterNoLoad */ 42, [matchValue.fields[0]])) : ((matchValue.tag === 37) ? (new JSONComponent_ComponentType(/* CounterNoEnable */ 43, [matchValue.fields[0]])) : ((matchValue.tag === 38) ? (new JSONComponent_ComponentType(/* CounterNoEnableLoad */ 44, [matchValue.fields[0]])) : ((matchValue.tag === 39) ? (new JSONComponent_ComponentType(/* AsyncROM1 */ 45, [matchValue.fields[0]])) : ((matchValue.tag === 40) ? (new JSONComponent_ComponentType(/* ROM1 */ 46, [matchValue.fields[0]])) : ((matchValue.tag === 41) ? (new JSONComponent_ComponentType(/* RAM1 */ 47, [matchValue.fields[0]])) : ((matchValue.tag === 42) ? (new JSONComponent_ComponentType(/* AsyncRAM1 */ 48, [matchValue.fields[0]])) : ((matchValue.tag === 43) ? (new JSONComponent_ComponentType(/* AsyncROM */ 49, [matchValue.fields[0]])) : ((matchValue.tag === 44) ? (new JSONComponent_ComponentType(/* ROM */ 50, [matchValue.fields[0]])) : ((matchValue.tag === 45) ? (new JSONComponent_ComponentType(/* RAM */ 51, [matchValue.fields[0]])) : ((matchValue.tag === 46) ? (new JSONComponent_ComponentType(/* Shift */ 52, [matchValue.fields[0], matchValue.fields[1], matchValue.fields[2]])) : ((matchValue.tag === 47) ? (new JSONComponent_ComponentType(/* BusCompare */ 53, [matchValue.fields[0], matchValue.fields[1]])) : ((matchValue.tag === 48) ? (new JSONComponent_ComponentType(/* Input */ 54, [matchValue.fields[0]])) : ((matchValue.tag === 49) ? (new JSONComponent_ComponentType(/* Constant */ 55, [matchValue.fields[0], matchValue.fields[1]])) : ((matchValue.tag === 50) ? (new JSONComponent_ComponentType(/* BusOut */ 56, [matchValue.fields[0]])) : ((matchValue.tag === 51) ? (new JSONComponent_ComponentType(/* MuxOut */ 57, [matchValue.fields[0]])) : ((matchValue.tag === 52) ? (new JSONComponent_ComponentType(/* JoinOut */ 58, [matchValue.fields[0], matchValue.fields[1]])) : ((matchValue.tag === 53) ? (new JSONComponent_ComponentType(/* JoinIn */ 59, [matchValue.fields[0], matchValue.fields[1]])) : ((matchValue.tag === 54) ? (new JSONComponent_ComponentType(/* ArrayMerge */ 60, [matchValue.fields[0]])) : ((matchValue.tag === 55) ? (new JSONComponent_ComponentType(/* ArrayMux */ 61, [matchValue.fields[0]])) : (new JSONComponent_ComponentType(/* Input1 */ 0, [matchValue.fields[0], matchValue.fields[1]])))))))))))))))))))))))))))))))))))))))))))))))))))))))));
    const jsonPort = (port) => (new JSONComponent_Port(int32ToString(pToInt(port.Id)), port.PortNumber, port.PortType, int32ToString(cToInt(port.HostId))));
    return new JSONComponent_Component(int32ToString(cToInt(comp.Id)), newType, comp.Label, map_1(jsonPort, comp.InputPorts), map_1(jsonPort, comp.OutputPorts), (option_1 = comp.SlotInfo, (option_1 != null) ? slotsToJson(option_1) : undefined), comp.X, comp.Y, comp.H, comp.W, (option_2 = comp.SymbolInfo, (option_2 != null) ? ((info = option_2, new JSONComponent_SymbolInfo(info.LabelBoundingBox, info.LabelRotation, info.STransform, info.ReversedInputPorts, ofList(map_1((tupledArg) => [toString(tupledArg[0]), tupledArg[1]], toList(info.PortOrientation)), {
        Compare: (x_1, y) => (comparePrimitives(x_1, y) | 0),
    }), map_2((_arg, ids) => map_1(toString, ids), info.PortOrder), info.HScale, info.VScale))) : undefined));
}

/**
 * A live connection to its file form, ids written as decimal strings.
 */
export function convertToJSONConnection(conn) {
    const jsonPort = (port) => (new JSONComponent_Port(int32ToString(pToInt(port.Id)), port.PortNumber, port.PortType, int32ToString(cToInt(port.HostId))));
    return new JSONComponent_Connection(int32ToString(conn.Id), jsonPort(conn.Source), jsonPort(conn.Target), conn.Vertices);
}

/**
 * JSComponent mapped to F# record.
 * Id uniquely identifies the component within a sheet.
 * Label is optional descriptor displayed on schematic.
 */
export class LegacyCanvas_LegacyComponent extends Record {
    constructor(Id, Type, Label, InputPorts, OutputPorts, X, Y, H, W) {
        super();
        this.Id = Id;
        this.Type = Type;
        this.Label = Label;
        this.InputPorts = InputPorts;
        this.OutputPorts = OutputPorts;
        this.X = X;
        this.Y = Y;
        this.H = H;
        this.W = W;
    }
}

export function LegacyCanvas_LegacyComponent_$reflection() {
    return record_type("CommonTypes.LegacyCanvas.LegacyComponent", [], LegacyCanvas_LegacyComponent, () => [["Id", string_type], ["Type", JSONComponent_ComponentType_$reflection()], ["Label", string_type], ["InputPorts", list_type(JSONComponent_Port_$reflection())], ["OutputPorts", list_type(JSONComponent_Port_$reflection())], ["X", float64_type], ["Y", float64_type], ["H", float64_type], ["W", float64_type]]);
}

/**
 * JSConnection mapped to F# record.
 * Id uniquely identifies connection globally and is used by library.
 */
export class LegacyCanvas_LegacyConnection extends Record {
    constructor(Id, Source, Target, Vertices) {
        super();
        this.Id = Id;
        this.Source = Source;
        this.Target = Target;
        this.Vertices = Vertices;
    }
}

export function LegacyCanvas_LegacyConnection_$reflection() {
    return record_type("CommonTypes.LegacyCanvas.LegacyConnection", [], LegacyCanvas_LegacyConnection, () => [["Id", string_type], ["Source", JSONComponent_Port_$reflection()], ["Target", JSONComponent_Port_$reflection()], ["Vertices", list_type(tuple_type(float64_type, float64_type))]]);
}

export function legacyTypesConvert(lComps, lConns) {
    return [map_1((comp) => (new JSONComponent_Component(comp.Id, comp.Type, comp.Label, comp.InputPorts, comp.OutputPorts, undefined, comp.X, comp.Y, comp.H, comp.W, undefined)), lComps), map_1((c) => (new JSONComponent_Connection(c.Id, c.Source, c.Target, map_1((_arg) => {
        if ((_arg[0] >= 0) && (_arg[1] >= 0)) {
            return [_arg[0], _arg[1], false];
        }
        else {
            return [Math.abs(_arg[0]), Math.abs(_arg[1]), true];
        }
    }, c.Vertices))), lConns)];
}

/**
 * unconfigured replaces Some -1, Error replaces None, Configured of int replaces Some (positive int)
 */
export class WireWidth extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Configured", "Unconfigured", "ErrorWidth"];
    }
    static Unconfigured = new WireWidth(1, []);
    static ErrorWidth = new WireWidth(2, []);
}

export function WireWidth_$reflection() {
    return union_type("CommonTypes.WireWidth", [], WireWidth, () => [[["Item", int32_type]], [], []]);
}

export class NumberBase extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Hex", "Dec", "Bin", "SDec"];
    }
    static Hex = new NumberBase(0, []);
    static Dec = new NumberBase(1, []);
    static Bin = new NumberBase(2, []);
    static SDec = new NumberBase(3, []);
}

export function NumberBase_$reflection() {
    return union_type("CommonTypes.NumberBase", [], NumberBase, () => [[], [], [], []]);
}

/**
 * Colors to highlight components
 * Case name is used as HTML color name.
 * See JSHelpers.getColorString
 * lots of colors can be added, see https://www.w3schools.com/colors/colors_names.asp
 * The Text() method converts it to the correct HTML string
 * Where speed matters the color must be added as a case in the match statement
 */
export class HighLightColor extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Red", "Blue", "Yellow", "Green", "Orange", "Grey", "White", "Purple", "DarkSlateGrey", "Thistle", "Brown", "SkyBlue"];
    }
    static Red = new HighLightColor(0, []);
    static Blue = new HighLightColor(1, []);
    static Yellow = new HighLightColor(2, []);
    static Green = new HighLightColor(3, []);
    static Orange = new HighLightColor(4, []);
    static Grey = new HighLightColor(5, []);
    static White = new HighLightColor(6, []);
    static Purple = new HighLightColor(7, []);
    static DarkSlateGrey = new HighLightColor(8, []);
    static Thistle = new HighLightColor(9, []);
    static Brown = new HighLightColor(10, []);
    static SkyBlue = new HighLightColor(11, []);
}

export function HighLightColor_$reflection() {
    return union_type("CommonTypes.HighLightColor", [], HighLightColor, () => [[], [], [], [], [], [], [], [], [], [], [], []]);
}

export function HighLightColor__Text(this$) {
    switch (this$.tag) {
        case 0:
            return "Red";
        case 1:
            return "Blue";
        case 11:
            return "Skyblue";
        case 2:
            return "Yellow";
        case 3:
            return "Green";
        case 5:
            return "Grey";
        case 7:
            return "Purple";
        case 8:
            return "darkslategrey";
        case 9:
            return "thistle";
        default:
            return toText(printf("%A"))(this$);
    }
}

/**
 * Uniquely identifies a wave by the component it comes from, and the port on which that
 * wave is from. Two waves can be identical but have a different index (e.g. a wave with
 * PortType Input must be driven by another wave of PortType Output).
 */
export class WaveIndexT extends Record {
    constructor(SimArrayIndex, Id, PortType, PortNumber) {
        super();
        this.SimArrayIndex = SimArrayIndex;
        this.Id = Id;
        this.PortType = PortType;
        this.PortNumber = (PortNumber | 0);
    }
}

export function WaveIndexT_$reflection() {
    return record_type("CommonTypes.WaveIndexT", [], WaveIndexT, () => [["SimArrayIndex", int32_type], ["Id", tuple_type(int32_type, list_type(int32_type))], ["PortType", PortType_$reflection()], ["PortNumber", int32_type]]);
}

/**
 * The stable NAME of a signal: which port of which component of which instance.
 * 
 * This is WaveIndexT without SimArrayIndex, and that omission is the point. SimArrayIndex is a
 * step-array index handed out by one build of one simulation and means nothing outside it,
 * which is why a selection has to be re-resolved by the other three fields whenever the
 * simulation is rebuilt. A SignalId survives a rebuild by construction, so it is what the model
 * should hold.
 */
export class SignalId extends Record {
    constructor(SigComp, SigPortType, SigPort) {
        super();
        this.SigComp = SigComp;
        this.SigPortType = SigPortType;
        this.SigPort = (SigPort | 0);
    }
}

export function SignalId_$reflection() {
    return record_type("CommonTypes.SignalId", [], SignalId, () => [["SigComp", tuple_type(int32_type, list_type(int32_type))], ["SigPortType", PortType_$reflection()], ["SigPort", int32_type]]);
}

export class WSConfig extends Record {
    constructor(LastClock, FirstClock, FontSize, FontWeight) {
        super();
        this.LastClock = (LastClock | 0);
        this.FirstClock = (FirstClock | 0);
        this.FontSize = (FontSize | 0);
        this.FontWeight = (FontWeight | 0);
    }
}

export function WSConfig_$reflection() {
    return record_type("CommonTypes.WSConfig", [], WSConfig, () => [["LastClock", int32_type], ["FirstClock", int32_type], ["FontSize", int32_type], ["FontWeight", int32_type]]);
}

export const waveSimMultipliers = ofArray([1, 2, 5, 10, 20, 50, 100, 200, 500, 1000]);

export const waveSimStepsOverflow = 3;

export const waveSimMaxArrayMargin = waveSimStepsOverflow + max_1(waveSimMultipliers, {
    Compare: (x, y) => (comparePrimitives(x, y) | 0),
});

/**
 * One saved waveform: a component named by the LABELS of the custom component instances entered
 * to reach it, then its own label, and which of its ports.
 * 
 * Not ids and not array indices, because this is written to a file and read back against a design
 * that has been edited since. See `WavePath.fs`, which resolves it both ways.
 * 
 * The field names are prefixed because F# resolves an unannotated record field to the last type
 * declaring it, and `PortType`/`PortNumber` are `WaveIndexT`'s.
 */
export class WavePath extends Record {
    constructor(WPLabels, WPPortType, WPPortNumber) {
        super();
        this.WPLabels = WPLabels;
        this.WPPortType = WPPortType;
        this.WPPortNumber = (WPPortNumber | 0);
    }
}

export function WavePath_$reflection() {
    return record_type("CommonTypes.WavePath", [], WavePath, () => [["WPLabels", list_type(string_type)], ["WPPortType", PortType_$reflection()], ["WPPortNumber", int32_type]]);
}

/**
 * Info saved by Wave Sim.
 * This info is not necessarilu uptodate with deletions or additions in the Diagram.
 * The wavesim code processing this will not fail if non-existent nets are referenced.
 */
export class SavedWaveInfo extends Record {
    constructor(SelectedWaves, Radix, WaveformColumnWidth, SelectedRams, SelectedFRams, WSConfig, ClkWidth, Cursor, LastClk, DisplayedPortIds) {
        super();
        this.SelectedWaves = SelectedWaves;
        this.Radix = Radix;
        this.WaveformColumnWidth = WaveformColumnWidth;
        this.SelectedRams = SelectedRams;
        this.SelectedFRams = SelectedFRams;
        this.WSConfig = WSConfig;
        this.ClkWidth = ClkWidth;
        this.Cursor = Cursor;
        this.LastClk = LastClk;
        this.DisplayedPortIds = DisplayedPortIds;
    }
}

export function SavedWaveInfo_$reflection() {
    return record_type("CommonTypes.SavedWaveInfo", [], SavedWaveInfo, () => [["SelectedWaves", option_type(list_type(WavePath_$reflection()))], ["Radix", option_type(NumberBase_$reflection())], ["WaveformColumnWidth", option_type(float64_type)], ["SelectedRams", option_type(class_type("Microsoft.FSharp.Collections.FSharpMap`2", [int32_type, string_type]))], ["SelectedFRams", option_type(list_type(tuple_type(list_type(string_type), string_type)))], ["WSConfig", option_type(WSConfig_$reflection())], ["ClkWidth", option_type(float64_type)], ["Cursor", option_type(uint32_type)], ["LastClk", option_type(uint32_type)], ["DisplayedPortIds", option_type(array_type(string_type))]]);
}

/**
 * Is this the name of a sheet the EXPANSION made, rather than one somebody drew?
 * 
 * A sheet name is a file name, so a path separator can never be typed into one - which is what
 * ArrayElaborate builds its generated names out of, and what makes this test exact rather than a
 * guess about naming. What it is asked for: such a sheet and the instances of it were made
 * together in one pass, so the checks that catch an instance drifting from a sheet somebody
 * edited have nothing to say about them.
 */
export function isGeneratedSheetName(sheetName) {
    return sheetName.indexOf("/") >= 0;
}

/**
 * Info regarding sheet saved in the .dgm file
 */
export class SheetInfo extends Record {
    constructor(Form, Description, ParameterDefinitions, IsTopSheet, ArrayInfo) {
        super();
        this.Form = Form;
        this.Description = Description;
        this.ParameterDefinitions = ParameterDefinitions;
        this.IsTopSheet = IsTopSheet;
        this.ArrayInfo = ArrayInfo;
    }
}

export function SheetInfo_$reflection() {
    return record_type("CommonTypes.SheetInfo", [], SheetInfo, () => [["Form", option_type(CCForm_$reflection())], ["Description", option_type(string_type)], ["ParameterDefinitions", option_type(ParameterDefs_$reflection())], ["IsTopSheet", option_type(bool_type)], ["ArrayInfo", option_type(ArrayInfo_$reflection())]]);
}

export class JSONWave_SavedWaveInfo extends Record {
    constructor(SelectedWaves, Radix, WaveformColumnWidth, SelectedRams, SelectedFRams, WSConfig, ClkWidth, Cursor, LastClk, DisplayedPortIds) {
        super();
        this.SelectedWaves = SelectedWaves;
        this.Radix = Radix;
        this.WaveformColumnWidth = WaveformColumnWidth;
        this.SelectedRams = SelectedRams;
        this.SelectedFRams = SelectedFRams;
        this.WSConfig = WSConfig;
        this.ClkWidth = ClkWidth;
        this.Cursor = Cursor;
        this.LastClk = LastClk;
        this.DisplayedPortIds = DisplayedPortIds;
    }
}

export function JSONWave_SavedWaveInfo_$reflection() {
    return record_type("CommonTypes.JSONWave.SavedWaveInfo", [], JSONWave_SavedWaveInfo, () => [["SelectedWaves", option_type(list_type(WavePath_$reflection()))], ["Radix", option_type(NumberBase_$reflection())], ["WaveformColumnWidth", option_type(float64_type)], ["SelectedRams", option_type(class_type("Microsoft.FSharp.Collections.FSharpMap`2", [string_type, string_type]))], ["SelectedFRams", option_type(list_type(tuple_type(list_type(string_type), string_type)))], ["WSConfig", option_type(WSConfig_$reflection())], ["ClkWidth", option_type(float64_type)], ["Cursor", option_type(uint32_type)], ["LastClk", option_type(uint32_type)], ["DisplayedPortIds", option_type(array_type(string_type))]]);
}

export class JSONWave_SheetInfo extends Record {
    constructor(Form, Description, ParameterDefinitions, IsTopSheet, ArrayInfo) {
        super();
        this.Form = Form;
        this.Description = Description;
        this.ParameterDefinitions = ParameterDefinitions;
        this.IsTopSheet = IsTopSheet;
        this.ArrayInfo = ArrayInfo;
    }
}

export function JSONWave_SheetInfo_$reflection() {
    return record_type("CommonTypes.JSONWave.SheetInfo", [], JSONWave_SheetInfo, () => [["Form", option_type(CCForm_$reflection())], ["Description", option_type(string_type)], ["ParameterDefinitions", option_type(JSONParams_ParameterDefs_$reflection())], ["IsTopSheet", option_type(bool_type)], ["ArrayInfo", option_type(ArrayInfo_$reflection())]]);
}

export function waveInfoToJson(wi) {
    let option_1;
    return new JSONWave_SavedWaveInfo(wi.SelectedWaves, wi.Radix, wi.WaveformColumnWidth, (option_1 = wi.SelectedRams, (option_1 != null) ? ofList(map_1((tupledArg) => [int32ToString(tupledArg[0]), tupledArg[1]], toList(option_1)), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    }) : undefined), wi.SelectedFRams, wi.WSConfig, wi.ClkWidth, wi.Cursor, wi.LastClk, wi.DisplayedPortIds);
}

export function waveInfoOfJson(mapCompId, wi) {
    let option_1;
    return new SavedWaveInfo(wi.SelectedWaves, wi.Radix, wi.WaveformColumnWidth, (option_1 = wi.SelectedRams, (option_1 != null) ? ofList(map_1((tupledArg) => [mapCompId(tupledArg[0]), tupledArg[1]], toList(option_1)), {
        Compare: (x, y) => (compare(x, y) | 0),
    }) : undefined), wi.SelectedFRams, wi.WSConfig, wi.ClkWidth, wi.Cursor, wi.LastClk, wi.DisplayedPortIds);
}

export function sheetInfoToJson(si) {
    let option_1;
    return new JSONWave_SheetInfo(si.Form, si.Description, (option_1 = si.ParameterDefinitions, (option_1 != null) ? paramDefsToJson(option_1) : undefined), si.IsTopSheet, si.ArrayInfo);
}

export function sheetInfoOfJson(mapCompId, si) {
    let option_1;
    return new SheetInfo(si.Form, si.Description, (option_1 = si.ParameterDefinitions, (option_1 != null) ? paramDefsOfJson(mapCompId, option_1) : undefined), si.IsTopSheet, si.ArrayInfo);
}

/**
 * Static data describing a schematic sheet loaded as a custom component.
 * Every sheet is always identified with a file from which it is loaded/saved.
 * Name is human readable (and is the filename - without extension) and identifies sheet.
 * File path is the sheet directory and name (with extension).
 * InputLabels, OutputLabels are the I/O connections.
 * The I/O connection integers are bus widths.
 * The I/O connection strings are human readable. The strings are guaranteed
 * to be unique in the I/O connection list. I.e. An input label may be the same
 * as an output label, but two input (or output) labels cannot be the same.
 * The position in the I/O connections list is important as it implicitly
 * indicates the port number. For example, the first element in the InputLabels
 * list is related to the Component's Port with PortNumber 0.
 * Two instances of a loaded component have the same LoadedComponent data.
 */
export class LoadedComponent extends Record {
    constructor(Name, TimeStamp, FilePath, WaveInfo, CanvasState, InputLabels, OutputLabels, LCParameterSlots, Form, LoadedComponentIsOutOfDate, Description, IsTopSheet, ArrayInfo) {
        super();
        this.Name = Name;
        this.TimeStamp = TimeStamp;
        this.FilePath = FilePath;
        this.WaveInfo = WaveInfo;
        this.CanvasState = CanvasState;
        this.InputLabels = InputLabels;
        this.OutputLabels = OutputLabels;
        this.LCParameterSlots = LCParameterSlots;
        this.Form = Form;
        this.LoadedComponentIsOutOfDate = LoadedComponentIsOutOfDate;
        this.Description = Description;
        this.IsTopSheet = IsTopSheet;
        this.ArrayInfo = ArrayInfo;
    }
}

export function LoadedComponent_$reflection() {
    return record_type("CommonTypes.LoadedComponent", [], LoadedComponent, () => [["Name", string_type], ["TimeStamp", class_type("System.DateTime")], ["FilePath", string_type], ["WaveInfo", option_type(SavedWaveInfo_$reflection())], ["CanvasState", tuple_type(list_type(Component_$reflection()), list_type(Connection_$reflection()))], ["InputLabels", list_type(tuple_type(string_type, int32_type))], ["OutputLabels", list_type(tuple_type(string_type, int32_type))], ["LCParameterSlots", option_type(ParameterDefs_$reflection())], ["Form", option_type(CCForm_$reflection())], ["LoadedComponentIsOutOfDate", bool_type], ["Description", option_type(string_type)], ["IsTopSheet", bool_type], ["ArrayInfo", option_type(ArrayInfo_$reflection())]]);
}

export const formOpt_ = [(a) => a.Form, (s) => ((a_1) => ((s != null) ? (new LoadedComponent(a_1.Name, a_1.TimeStamp, a_1.FilePath, a_1.WaveInfo, a_1.CanvasState, a_1.InputLabels, a_1.OutputLabels, a_1.LCParameterSlots, s, a_1.LoadedComponentIsOutOfDate, a_1.Description, a_1.IsTopSheet, a_1.ArrayInfo)) : a_1))];

export const canvasState_ = [(a) => a.CanvasState, (s) => ((a_1) => (new LoadedComponent(a_1.Name, a_1.TimeStamp, a_1.FilePath, a_1.WaveInfo, s, a_1.InputLabels, a_1.OutputLabels, a_1.LCParameterSlots, a_1.Form, a_1.LoadedComponentIsOutOfDate, a_1.Description, a_1.IsTopSheet, a_1.ArrayInfo)))];

export const loadedComponentIsOutOfDate_ = [(a) => a.LoadedComponentIsOutOfDate, (s) => ((a_1) => (new LoadedComponent(a_1.Name, a_1.TimeStamp, a_1.FilePath, a_1.WaveInfo, a_1.CanvasState, a_1.InputLabels, a_1.OutputLabels, a_1.LCParameterSlots, a_1.Form, s, a_1.Description, a_1.IsTopSheet, a_1.ArrayInfo)))];

export const componentsState_ = Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, Optics_fst_())(canvasState_);

export const lcParameterSlots_ = [(a) => a.LCParameterSlots, (s) => ((a_1) => (new LoadedComponent(a_1.Name, a_1.TimeStamp, a_1.FilePath, a_1.WaveInfo, a_1.CanvasState, a_1.InputLabels, a_1.OutputLabels, s, a_1.Form, a_1.LoadedComponentIsOutOfDate, a_1.Description, a_1.IsTopSheet, a_1.ArrayInfo)))];

export const lcParameterDefs_ = [(a) => defaultArg(a.LCParameterSlots, new ParameterDefs(empty({
    Compare: (x, y) => (compare(x, y) | 0),
}), empty({
    Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
}))), (s) => ((a_1) => (new LoadedComponent(a_1.Name, a_1.TimeStamp, a_1.FilePath, a_1.WaveInfo, a_1.CanvasState, a_1.InputLabels, a_1.OutputLabels, s, a_1.Form, a_1.LoadedComponentIsOutOfDate, a_1.Description, a_1.IsTopSheet, a_1.ArrayInfo)))];

export const isTopSheet_ = [(a) => a.IsTopSheet, (s) => ((a_1) => (new LoadedComponent(a_1.Name, a_1.TimeStamp, a_1.FilePath, a_1.WaveInfo, a_1.CanvasState, a_1.InputLabels, a_1.OutputLabels, a_1.LCParameterSlots, a_1.Form, a_1.LoadedComponentIsOutOfDate, a_1.Description, s, a_1.ArrayInfo)))];

export const arrayInfo_ = [(a) => a.ArrayInfo, (s) => ((a_1) => (new LoadedComponent(a_1.Name, a_1.TimeStamp, a_1.FilePath, a_1.WaveInfo, a_1.CanvasState, a_1.InputLabels, a_1.OutputLabels, a_1.LCParameterSlots, a_1.Form, a_1.LoadedComponentIsOutOfDate, a_1.Description, a_1.IsTopSheet, s)))];

/**
 * Whether a sheet is an array design sheet.
 */
export function isArraySheet(ldc) {
    return ldc.ArrayInfo != null;
}

/**
 * Whether a component is clocked in itself - without looking inside a custom component, which is
 * a question about the sheet it names rather than about the component.
 * 
 * The same set as `SynchronousUtils.couldBeSynchronousComponent` minus `Custom`, which that one
 * has to assume is clocked and this one answers properly. They are two lists because this is in
 * CommonTypes, which the simulator is compiled after; `Properties.fs` holds them to agreeing.
 * 
 * AsyncROM1 is absent deliberately: an asynchronous ROM presents its data in the same cycle as
 * its address, so it is combinational. The three legacy memory types are absent because they
 * never occur - every loaded design has the `1` forms, and the evaluators raise on the others -
 * which is what this used to match, so a sheet whose only clocked component was a memory was
 * reported combinational and drawn as if it were.
 */
export function isClockedPrimitive(compType) {
    switch (compType.tag) {
        case 31:
        case 32:
        case 33:
        case 34:
        case 40:
        case 41:
        case 42:
        case 35:
        case 37:
        case 36:
        case 38:
            return true;
        default:
            return false;
    }
}

/**
 * Which SHEETS of a design hold something clocked, at any depth.
 * 
 * A fact about a sheet and not about any instance of it: a sheet's components are the same
 * wherever it is placed, and parameters change widths rather than kinds. So it is worked out once
 * for a design, and every symbol drawn on it is then a set lookup.
 * 
 * Settled in one pass over sheets ordered so that each comes after everything inside it. Working
 * it out per component instead re-walked the whole subtree for every custom component drawn -
 * twice, since a symbol asks once for its colour and once for its own record - and that walk
 * follows every ROUTE through the design rather than every sheet, so a design whose sheets
 * instantiate one another costs paths rather than sheets.
 */
export function clockedSheets(ldcs) {
    const byName = ofList(map_1((ldc) => [ldc.Name, ldc], ldcs), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
    let sorted;
    const walk = (tupledArg, name_1) => {
        let matchValue;
        const seen = tupledArg[0];
        const acc = tupledArg[1];
        if (contains(name_1, seen)) {
            return [seen, acc];
        }
        else {
            const patternInput = fold(walk, [add(name_1, seen), acc], (matchValue = tryFind_1(name_1, byName), (matchValue != null) ? List_distinct(choose((comp) => {
                const matchValue_1 = comp.Type;
                if (matchValue_1.tag === 26) {
                    return matchValue_1.fields[0].Name;
                }
                else {
                    return undefined;
                }
            }, matchValue.CanvasState[0]), {
                Equals: (x_1, y_1) => (x_1 === y_1),
                GetHashCode: (x_1) => (stringHash(x_1) | 0),
            }) : empty_1()));
            return [patternInput[0], cons(name_1, patternInput[1])];
        }
    };
    sorted = reverse(fold(walk, [empty_2({
        Compare: (x_2, y_2) => (comparePrimitives(x_2, y_2) | 0),
    }), empty_1()], map_1((ldc_2) => ldc_2.Name, ldcs))[1]);
    return fold((clocked, name_2) => {
        let matchValue_2;
        if ((matchValue_2 = tryFind_1(name_2, byName), (matchValue_2 != null) && exists((comp_1) => {
            const matchValue_3 = comp_1.Type;
            if (matchValue_3.tag === 26) {
                return contains(matchValue_3.fields[0].Name, clocked);
            }
            else {
                return isClockedPrimitive(matchValue_3);
            }
        }, matchValue_2.CanvasState[0]))) {
            return add(name_2, clocked);
        }
        else {
            return clocked;
        }
    }, empty_2({
        Compare: (x_3, y_3) => (comparePrimitives(x_3, y_3) | 0),
    }), sorted);
}

/**
 * Whether a component is clocked, given which sheets of the design are.
 */
export function isClockedGiven(clocked, comp) {
    const matchValue = comp.Type;
    if (matchValue.tag === 26) {
        return contains(matchValue.fields[0].Name, clocked);
    }
    else {
        return isClockedPrimitive(matchValue);
    }
}

/**
 * Returns true if a component is clocked.
 * 
 * For ONE component. Anything asking about many should work out `clockedSheets` once and use
 * `isClockedGiven` - which is what the draw block does, since it asks about every symbol on a
 * sheet twice over.
 */
export function isClocked(_visitedSheets, ldcs, comp) {
    const matchValue = comp.Type;
    if (matchValue.tag === 26) {
        return isClockedGiven(clockedSheets(ldcs), comp);
    }
    else {
        return isClockedPrimitive(matchValue);
    }
}

/**
 * Type for an open project which represents a complete design.
 * ProjectPath is directory containing project files.
 * OpenFileName is name of file from which current schematic sheet is loaded/saved, without extension or path
 * LoadedComponents contains the list of schematic sheets, each as a component, one per sheet.
 */
export class Project extends Record {
    constructor(ProjectPath, OpenFileName, WorkingFileName, LoadedComponents) {
        super();
        this.ProjectPath = ProjectPath;
        this.OpenFileName = OpenFileName;
        this.WorkingFileName = WorkingFileName;
        this.LoadedComponents = LoadedComponents;
    }
}

export function Project_$reflection() {
    return record_type("CommonTypes.Project", [], Project, () => [["ProjectPath", string_type], ["OpenFileName", string_type], ["WorkingFileName", option_type(string_type)], ["LoadedComponents", list_type(LoadedComponent_$reflection())]]);
}

export const loadedComponents_ = [(a) => a.LoadedComponents, (s) => ((a_1) => (new Project(a_1.ProjectPath, a_1.OpenFileName, a_1.WorkingFileName, s)))];

export const openLoadedComponent_ = [(a) => find((lc) => (lc.Name === a.OpenFileName), a.LoadedComponents), (lc$0027) => ((a_1) => (new Project(a_1.ProjectPath, a_1.OpenFileName, a_1.WorkingFileName, map_1((lc_1) => ((lc_1.Name === a_1.OpenFileName) ? lc$0027 : lc_1), a_1.LoadedComponents))))];

export const openFileName_ = [(a) => a.OpenFileName, (s) => ((a_1) => (new Project(a_1.ProjectPath, s, a_1.WorkingFileName, a_1.LoadedComponents)))];

export const workingFileName_ = [(a) => a.WorkingFileName, (s) => ((a_1) => (new Project(a_1.ProjectPath, a_1.OpenFileName, s, a_1.LoadedComponents)))];

export function loadedComponentOf_(name) {
    return [(a) => find((lc) => (lc.Name === name), a.LoadedComponents), (lc$0027) => ((a_1) => (new Project(a_1.ProjectPath, a_1.OpenFileName, a_1.WorkingFileName, map_1((lc_1) => ((lc_1.Name === name) ? lc$0027 : lc_1), a_1.LoadedComponents))))];
}

/**
 * Documents user circuit error found during connection width inference
 */
export class WidthInferError extends Record {
    constructor(Msg, ConnectionsAffected) {
        super();
        this.Msg = Msg;
        this.ConnectionsAffected = ConnectionsAffected;
    }
}

export function WidthInferError_$reflection() {
    return record_type("CommonTypes.WidthInferError", [], WidthInferError, () => [["Msg", string_type], ["ConnectionsAffected", list_type(int32_type)]]);
}

/**
 * Messages sent from draw block
 */
export class JSDiagramMsg extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["InitCanvas", "SelectComponent", "UnselectComponent", "InferWidths", "SetHasUnsavedChanges"];
    }
}

export function JSDiagramMsg_$reflection() {
    return union_type("CommonTypes.JSDiagramMsg", [], JSDiagramMsg, () => [[["Item", tuple_type(list_type(Component_$reflection()), list_type(Connection_$reflection()))]], [["Item", Component_$reflection()]], [["Item", unit_type]], [["Item", unit_type]], [["Item", bool_type]]]);
}

/**
 * Keeps track of what cursor to show
 */
export class CursorType extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Default", "ClickablePort", "NoCursor", "Spinner", "GrabWire", "GrabLabel", "GrabSymbol", "Grabbing", "ResizeNESW", "ResizeNWSE"];
    }
    static Default = new CursorType(0, []);
    static ClickablePort = new CursorType(1, []);
    static NoCursor = new CursorType(2, []);
    static Spinner = new CursorType(3, []);
    static GrabWire = new CursorType(4, []);
    static GrabLabel = new CursorType(5, []);
    static GrabSymbol = new CursorType(6, []);
    static Grabbing = new CursorType(7, []);
    static ResizeNESW = new CursorType(8, []);
    static ResizeNWSE = new CursorType(9, []);
}

export function CursorType_$reflection() {
    return union_type("CommonTypes.CursorType", [], CursorType, () => [[], [], [], [], [], [], [], [], [], []]);
}

export function CursorType__Text(this$) {
    switch (this$.tag) {
        case 1:
            return "move";
        case 2:
            return "none";
        case 3:
            return "wait";
        case 4:
            return "crosshair";
        case 6:
            return "cell";
        case 5:
            return "grab";
        case 7:
            return "grabbing";
        case 8:
            return "nesw-resize";
        case 9:
            return "nwse-resize";
        default:
            return "default";
    }
}

/**
 * Type capturing global key press information
 * TODO: use this consistently, with global key listener,
 * throughout Issie.
 */
export class KeyPressInfo extends Record {
    constructor(ShiftKey, ControlKey, AltKey, MetaKey, KeyString) {
        super();
        this.ShiftKey = ShiftKey;
        this.ControlKey = ControlKey;
        this.AltKey = AltKey;
        this.MetaKey = MetaKey;
        this.KeyString = KeyString;
    }
}

export function KeyPressInfo_$reflection() {
    return record_type("CommonTypes.KeyPressInfo", [], KeyPressInfo, () => [["ShiftKey", bool_type], ["ControlKey", bool_type], ["AltKey", bool_type], ["MetaKey", bool_type], ["KeyString", string_type]]);
}

